import axios from "axios";
import { proxyUrl } from "@/config";
import { setupInterceptorsTo } from "./Interceptors";
import { SavingsPlanUserIdentityType } from "./SavingsPlanUserIdentityService";
import { BmePortfolioType } from "./BmePortfolioService";
import { ManagedPortfolioList, ManagedPortfolioType } from "./ManagedPortfolioService";
import { FnmContract } from "./FnmContractService";
import { UserType } from "./UserService";
import { SavingsPlanProxyType } from "./SavingsPlanProxyService";
import { UsersSubscriptionType } from "./UsersSubscriptionService";
import { ITableSPlansRelational } from "@/components/SavePlans/TableRelationPlans";
import { ElementAmountType } from "./ElementsService";
import { ITableSPlansActive, SavingsPlanAndStatusLite } from "@/components/SavePlans/types/SavePlansTypes";
import { OnboardingEnvelope, PlanOnboardingState, PlanRiskProfileEvaluation, mapAxiosErrorToOnboardingEnvelope, normalizePlanOnboardingState, normalizeRiskProfileEvaluation,	parseOnboardingEnvelopeWire } from "./SavingsPlanOnboarding.types";
setupInterceptorsTo(axios);

export enum SavingsPlanTypes
{
	Saver,
	Alchemy,
	Advanced,
	Promoted,
	Experience,
	FinancialEducation,
	ExperienceSample
}

export enum SavingsPlanStatuses
{
	New,
	Pending,
	Validated,
	SuitabilitySigned,
	Active,
	Outdated,
	Cancelled,
	WaitingTPVConfirmation,
	DocumentationUpdate,
	Incidence
}

interface ISavingsPlan
{
	id?: number,
	userId?: number,
	user?: UserType,
	name: string,
	initialContribution?: number,
	periodicContribution?: number,
	periodicContributionPeriod?: number,
	configurationMapStep?: string,
	investForFree?: boolean,
	transferPortfolio?: boolean,
	normalInvestment?: boolean,
	managementInvestment?: boolean,
	managerId?: number,
	advisedInvestment?: boolean,
	adviserId?: number,
	createdAt?: string,
	type: SavingsPlanTypes,
	endDate?: string,
	categories?: any[],
	profitability?: number,
	status?: number,
	savingsPlansUserIdentities?: any[],
	bmePortfolioId?: number,
	bmePortfolio?: BmePortfolioType,
	managedPortfolioId?: number,
	managedPortfolio?: ManagedPortfolioType,
	fnmContractId?: number,
	fnmContract?: FnmContract,
	savingsPlansProxies?: SavingsPlanProxyType[],
	usersSubscription?: UsersSubscriptionType,
	elementAmountId?: number,
	elementAmount?: ElementAmountType
}
export type SavingsPlanType = ISavingsPlan;

interface IDefaultSavingsPlan
{
	id: number,
	name: string,
	userId: number,
	fundName: string,
	isin: string,
	fundDescription: string,
	minAmount: number
}
export type DefaultSavingPlan = IDefaultSavingsPlan;

interface IReadySavingPlan
{
	hasPortfolio: boolean,
	readySavingPlan: boolean
}
export type ReadySavingPlan = IReadySavingPlan;

interface IPlansAndIdentities
{
	id: number,
	planName: string,
	identities: Identities[]
}

interface IIdentities
{
	name: string,
	dni: string,
}

interface IItems
{
	key: string,
	name: string,
	dni: string
}

interface IItemsAndGroup
{
	key: string,
	name: string,
	dni: string,
	startIndex: number,
	count: number
}

interface IAuxIdentities
{
	key: string,
	startIndex: number,
	count: number,
	name: string
}

interface ISavingsPlanSameIdentities
{
	userId: number,
	subscriptionId: number
}

interface IMapConfigurationData
{
	isMapEnabled: boolean,
	status: number,
	isPlanValidAndPaid: boolean,
	isSaverPlan: boolean,
	isAlchemyPlan: boolean,
	isAdvancedPlan: boolean,
	isSponsoredPlan: boolean,
	isExperiencePlan: boolean,
	mapStep: string,
	revisionTime: string | undefined,
	repaymentObject: IRepaymentObject
}

interface IRepaymentObject
{
	subscriptionId: number,
	userSubscriptionId: number,
	limitAmount: number,
	type: string
}

export type MapConfigurationData = IMapConfigurationData;
export type MapConfigurationRepaymentObject = IRepaymentObject;
export type SavingsPlanSameIdentities = ISavingsPlanSameIdentities;
export type PlansAndIdentities = IPlansAndIdentities;
export type Identities = IIdentities;
export type AuxIdentities = IAuxIdentities;
export type Items = IItems;
export type ItemGroup = IItemsAndGroup;

interface IActiveSubscriptionAndSavingPlans
{
	id: number,
	planName: string,
	type: number,
	selected: boolean,
	subscriptionId: number,
	fnmContractId: number,
	finametrixContract: string,
	createdAt?: Date,
	finametrixId: string,
	allUserIdentitiesHaveSuitabilityTest: boolean,
	isSubscribedToElements: boolean
}
export type ActiveSubscriptionAndSavingPlans = IActiveSubscriptionAndSavingPlans;

interface IPortfolioValue
{
	savingPlanId: number,
	finametrixContract: string,
	portfolioValue: number,
	returnValue: number
}

export type PortfolioValue = IPortfolioValue;
class SavingsPlanService
{
	public checkPermissionOnSavingPlans = async (savingsPlanId: number, userId: number): Promise<boolean> =>
	{
		let url: string = `${proxyUrl}/checkSavingPlan/${savingsPlanId}/${userId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getSavingsPlan = async (savingsPlanId: number): Promise<SavingsPlanType> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/${savingsPlanId}`;
		const req = await axios.get<any>(url);
		return req.data;
	}
	public getSavingsPlansAndStatus = async (userId: number): Promise<ITableSPlansActive[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getSavingsPlansAndStatus/${userId}`;
		const req = await axios.get(url);
		return req.data;
	}
    public getSavingsPlansAndStatusLite = async (userId: number): Promise<SavingsPlanAndStatusLite[]> =>
    {
        let url: string = `${proxyUrl}/savingsPlans/getSavingsPlansLite/${userId}`;
        const req = await axios.get(url);
        return req.data;
    }
	public getRelationalPlans = async (userId: number): Promise<ITableSPlansRelational[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/relationalSavingPlan/${userId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getActiveSavingPlans = async (userId: number): Promise<ITableSPlansActive[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getActiveSavingPlans/${userId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getFullActivePlanById = async (id: number): Promise<ITableSPlansActive> =>
	{
		let url: string = `${proxyUrl}/GetFullActivePlanById/${id}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getActiveSubscriptionAndSavingPlan = async (userId: number): Promise<ActiveSubscriptionAndSavingPlans[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getActiveSubscriptionAndSavingPlan/${userId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getConfiguringSavingPlans = async (userId: number): Promise<ITableSPlansActive[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getConfiguringSavingPlans/${userId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getPromotionedSavingPlan = async (userId: number): Promise<DefaultSavingPlan> =>
	{
		let url: string = `${proxyUrl}/promotionedSavingPlan/${userId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getReadyPromotionedPlan = async (userId: number, id: number): Promise<IReadySavingPlan> =>
	{
		let url: string = `${proxyUrl}/readySavingPlan/${userId}/${id}`;
		const req = await axios.get(url);
		return req.data;
	}
	public createSavingsPlan = async (savingsPlan: SavingsPlanType): Promise<SavingsPlanType> =>
	{
		let url: string = `${proxyUrl}/savingsPlans`;
		const req = await axios.post(url, savingsPlan);
		return req.data;
	}
	public patchSavingsPlan = async (savingsPlanId: number, savingsPlan: any): Promise<SavingsPlanType> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/${savingsPlanId}`;
		const req = await axios.patch(url, savingsPlan);
		return req.data;
	}
	public updateSavingPlanName = async (planId: number, userId: number, planName: string): Promise<boolean> =>
	{
		let url: string = `${proxyUrl}/UpdateSavingPlan/${userId}/${planId}`;
		const req = await axios.patch(url, planName);
		return req.data;
	}
	public getSavingsPlansByUserId = async (userId: number): Promise<SavingsPlanType[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getByUserId/${userId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getSavingsPlansByType = async (userId: number, type: number): Promise<PlansAndIdentities[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getByType/${userId}/${type}`;
		const req = await axios.get(url);
		return req.data;
	}
	public getCompleteSavingsPlansByUserId = async (userId: number): Promise<SavingsPlanType[]> =>
	{
		return this.getSavingsPlansByUserIdAndStatus(userId, 1);
	}
	public getIncompleteSavingsPlansByUserId = async (userId: number): Promise<SavingsPlanType[]> =>
	{
		return this.getSavingsPlansByUserIdAndStatus(userId, 0);
	}
	private getSavingsPlansByUserIdAndStatus = async (userId: number, status: number): Promise<SavingsPlanType[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getByUserId/${userId}?status=${status}`;
		const req = await axios.get(url);
		return req.data;
	}
	public addSignaturesToSavingsPlanUserIdentities = async (savingsPlanId: number): Promise<SavingsPlanUserIdentityType[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/${savingsPlanId}/signatures`;
		const req = await axios.post(url);
		return req.data;
	}
	public addManagementAuthorizationSignaturesToSavingsPlanUserIdentities = async (savingsPlanId: number): Promise<SavingsPlanUserIdentityType[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/${savingsPlanId}/managementAuthorizationSignatures`;
		const req = await axios.post(url);
		return req.data;
	}
	public addSuitabilityTestSignaturesToSavingsPlanUserIdentities = async (savingsPlanId: number): Promise<SavingsPlanUserIdentityType[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/${savingsPlanId}/suitabilityTestSignatures`;
		const req = await axios.post(url);
		return req.data;
	}
	public getLastNewSavingsPlanByUsersSubscriptionId = async (usersSubscriptionId: number): Promise<SavingsPlanType> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getLastNewByUsersSubscriptionId/${usersSubscriptionId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public addSavingPlanSameIdentities = async (savingsPlanId: number): Promise<any> =>
	{
		let url: string = `${proxyUrl}/savingPlans/${savingsPlanId}/sameIdentities`;
		const req = await axios.post(url, {});
		return req.data;
	}
	public getMapConfigurationData = async (userId: number, savingPlanId: number): Promise<MapConfigurationData> =>
	{
		let url: string = `${proxyUrl}/mapConfigurationData/${userId}/${savingPlanId}`;
		const req = await axios.get(url);
		return req.data;
	}
	public deleteSavingPlanOnConfigure = async (savingsPlanId: number): Promise<void> =>
	{
		let url: string = `${proxyUrl}/savingsPlan/${savingsPlanId}/onConfigure`;
		const req = await axios.delete(url);
		return req.data;
	}
	public getDistributionFromSavingPlanId = async (savingPlanId: number): Promise<ManagedPortfolioList[]> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/${savingPlanId}/currentDistribution`;
		const req = await axios.get<any>(url);
		return req.data;
	}
	public getHTMLContract = async (savingsPlanId: number): Promise<string> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/${savingsPlanId}/html`;
		const req = await axios.get<any>(url);
		return req.data;
	}
	public getHTMLContractBySignatureGuid = async (signatureGuid: string): Promise<string> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getHTMLContractBySignatureGuid/${signatureGuid}`;
		const req = await axios.get<any>(url);
		return req.data;
	}
	public getHTMLTripartiteContract = async (signatureGuid: string): Promise<string> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/getHtmlTripartiteContractBySignatureGuid/${signatureGuid}`;
		const req = await axios.get<any>(url);
		return req.data;
	}
	public rebalance = async (savingsPlanId: number, modelPortfolioId: number, typeOfElementUsed: string ): Promise<any> =>
	{
		let url: string = `${proxyUrl}/savingsPlans/${savingsPlanId}/rebalance/${modelPortfolioId}/${typeOfElementUsed}`;
		const req = await axios.get<any>(url);
		return req.data;
	}
	public getRebalanceStatus = async (processId: number): Promise<any> =>
	{
		const url = `${proxyUrl}/savingsPlans/rebalance/${processId}/status`;
		const response = await axios.get<any>(url);
		return response.data;
	}
	public getLatestPortfolioValue = async (savingsPlanId: number, userId: number): Promise<PortfolioValue> =>
	{
		let url: string = `${proxyUrl}/GetLatestPortfolioValue/${userId}/${savingsPlanId}`;
		const req = await axios.get<any>(url);
		return req.data;
	}
	public postPortfolioValue = async (data: any): Promise<boolean> =>
	{
		let url: string = `${proxyUrl}/PostPortfolioValue`;
		const req = await axios.post(url, data, {headers: { "content-type": "application/json" }});
		return req.data;
	}

    // Nueva zona contrato
	public getPlanOnboardingState = async (savingsPlanId: number): Promise<OnboardingEnvelope<PlanOnboardingState>> =>
	{
		try
		{
			const url = `${proxyUrl}/savingsPlans/${savingsPlanId}/onboardingState`;
			const response = await axios.get(url);
			return parseOnboardingEnvelopeWire(response.data, normalizePlanOnboardingState);
		}
		catch(error: any)
		{
			return mapAxiosErrorToOnboardingEnvelope(error, normalizePlanOnboardingState);
		}
	}
	// expectedStep es la guarda opcional de sincronía: la clave del paso que la pantalla cree actual
	// (p. ej. "2.1"). Si el backend ve que el plan está en otro paso, rechaza con STEP_MISMATCH en vez
	// de avanzar un paso distinto. No viaja en la URL de navegación, es un parámetro de la llamada API.
	public advancePlanStep = async (savingsPlanId: number, expectedStep?: string): Promise<OnboardingEnvelope<PlanOnboardingState>> =>
	{
		try
		{
			const query = expectedStep ? `?expectedStep=${encodeURIComponent(expectedStep)}` : "";
			const url = `${proxyUrl}/savingsPlans/${savingsPlanId}/advanceStep${query}`;
			const response = await axios.post(url);
			return parseOnboardingEnvelopeWire(response.data, normalizePlanOnboardingState);
		}
		catch(error: any)
		{
			return mapAxiosErrorToOnboardingEnvelope(error, normalizePlanOnboardingState);
		}
	}
	public getRiskProfileEvaluation = async (savingsPlanId: number): Promise<OnboardingEnvelope<PlanRiskProfileEvaluation>> =>
	{
		try
		{
			const url = `${proxyUrl}/savingsPlans/${savingsPlanId}/riskProfileEvaluation`;
			const response = await axios.get(url);
			return parseOnboardingEnvelopeWire(response.data, normalizeRiskProfileEvaluation);
		}
		catch(error: any)
		{
			return mapAxiosErrorToOnboardingEnvelope(error, normalizeRiskProfileEvaluation);
		}
	}
	public enterMinorToAdultFlow = async (savingsPlanId: number): Promise<OnboardingEnvelope<PlanOnboardingState>> =>
	{
		try
		{
			const url = `${proxyUrl}/savingsPlans/${savingsPlanId}/minorToAdult/enter`;
			const response = await axios.post(url);
			return parseOnboardingEnvelopeWire(response.data, normalizePlanOnboardingState);
		}
		catch(error: any)
		{
			return mapAxiosErrorToOnboardingEnvelope(error, normalizePlanOnboardingState);
		}
	}
}

const savingsPlans = new SavingsPlanService();
export default savingsPlans;